/**
 * Responsible for creating a slider zoom or in other words a carousel where the
 * images in the carousel can be interacted with so that they can be zoomed in
 * @module modules/zoom/sliderZoom
 */
import $ from 'jquery';
import _get from 'lodash/get';
import $script from 'scriptjs';
import '../slick-carousel';

import customPaging from '../slick/custom-paging';
import { get } from '../../lib/props';
import DialogFunc from '../dialog/dialog';
import imageZoom from './image-zoom';
import Keyboard from '../keyboard';

const HAMMERJS_FALLBACK = 'https://ajax.googleapis.com/ajax/libs/hammerjs/2.0.8/hammer.min.js';
const renderNoop = () => '';

/**
 * Makes a request for HammerJS.
 * @private
 * @return {Promise} Denotes when the request has been successfully executed
 */
const loadHammerJSFunc = () =>
  new Promise((resolve) => {
    $script(get('scripts.hammerJS', HAMMERJS_FALLBACK), () => {
      resolve(window.Hammer);
    });
  });

/**
 * Defines the options which can be provided for the image zoom.
 * @type {Object}
 */
const DEFAULTS = {
  Dialog: DialogFunc,
  loadHammerJS: loadHammerJSFunc,
  startingIndex: 0,
  speed: 300,
  abTest: window.rcProps && window.rcProps.pdp && window.rcProps.pdp.zoomImageThumbnails === true,
  slideFunc: num => num,
  menuRender: renderNoop,
  beforeContentRender: renderNoop,
  didRender: () => {},
  beforeUnmount: () => {},
  didUnmount: () => {},
  didSlideTransition: (prev, current) => ({ prev, current }),
  zoomClickListener: () => { },
  noImages: '',
  trapFocus: '',
  trapFocusFunc: Keyboard.trapFocus.bind(Keyboard),
  releaseFocusFunc: Keyboard.releaseFocus.bind(Keyboard),
};

/**
 * Defines the id for the close element.
 * @type {String}
 */
export const CLOSE_ID = 'close-zoom-menu';

/**
 * Defines the id for the zoom slider.
 * @type {String}
 */
export const ZOOM_SLIDER_ID = 'zoom-slider';

/**
 * Defines the id for the zoom slider.
 * @type {String}
 */
export const ZOOM_THUMB_ID = 'zoom-thumb';

/**
 * Defines the content className
 * @type {String}
 */
export const CONTENT_CLASSNAME = 'zoom-slider__content';

const preloadImages = (images, startingIndex) =>
  new Promise((resolve, reject) => {
    let hasStartingImage = false;
    const loadEvt = 'load';
    const errorEvt = 'error';

    images.forEach((image, i) => {
      const img = new Image();
      if (i === startingIndex) {
        hasStartingImage = true;
        let imageLoadCallback;
        let imageErrorCallback;

        const cleanup = () => {
          img.removeEventListener(loadEvt, imageLoadCallback);
          img.removeEventListener(errorEvt, imageErrorCallback);
        };

        img.addEventListener(loadEvt, imageLoadCallback = () => {
          resolve(img);
          cleanup();
        });
        img.addEventListener(errorEvt, imageErrorCallback = (err) => {
          reject(err);
          cleanup();
        });
      }

      img.src = image;
    });

    // Just in case a starting image isn't found, resolve the promise so it
    // isn't indefinitely pending.
    if (!hasStartingImage) {
      resolve();
    }
  });

/**
 * Binds an event to the close element
 * @private
 * @param  {Function} callback - The event handler for the close element
 */
const bindClose = (callback) => {
  const $closeButton = $(document.getElementById(CLOSE_ID));

  // Ensure the close button exists before binding
  if ($closeButton.length) {
    // Bind click event to the close button
    $closeButton.on('click.closeZoom', callback);
  } else {
    console.warn(`Element with ID "${CLOSE_ID}" not found.`);
  }

  // Bind keydown event to the document for the Escape key
  $(document).on('keydown.closeZoom', (e) => {
    if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
      if (!$('.dialog__container').is(':visible')) return;
      e.preventDefault();
      callback();
    }
  });
};

const logCloseZoom = () => {
  if (typeof window.logEngagement === 'function') {
    window.logEngagement(window.location.href, 'pdp zoom modal', 'close zoom');
  }
};

/**
 * Closes the dialog.
 * @private
 * @param  {Dialog} dialog - Instance of a dialog
 * @param  {Function} didUnmount - Called once the dialog has closed
 */
const closeDialog = (dialog, beforeUnmount, didUnmount) => {
  //  beforeUnmount();
  dialog.remove().then(() => {
    didUnmount();
  });

  // Unbind the click and keydown events on close
  $('.image-carousel').off('click.product-zoom');
  $('.image-carousel').off('keydown.product-zoom');

  // Focus back on the parent element
  $('.image-carousel').focus();

  logCloseZoom();
};

/**
 * Stupid helper that concats the element id based on an id for a slide.
 * @private
 * @param  {string} id - The id of the slide being requested
 * @return {string} - A slide id
 */
const getSlideId = id => `zoom-slider__item-${id}`;

/**
 * Obtains a slide or image basd on the id provided in the template.
 * @private
 * @param  {number} id - The id of the slide being requested
 * @return {HTMLElement} An element representing a slide or image
 */
const getSlide = id => document.getElementById(getSlideId(id));

/**
 * Executes in the future the close callback. The callback is responsible for
 * clean up to ensure nothing is left in memory.
 * @private
 * @param  {jQuery} $zoomSlider
 * @param  {Hammer} hammer - An instance of Hammer from hammerjs
 * @param  {Dialog} dialog - An instance of a dialog
 * @param  {Function} didUnmount - Used to denote when closed
 * @return {Function} Callback function used for the event
 */
const teardownFull = ($zoomSlider, hammer, dialog, beforeUnmount,
  didUnmount, zoomClickListener) => () => {
    hammer.destroy();
    dialog.$el.on('remove', () => $zoomSlider.slick('unslick'));
    // TODO: Find out the preferred interaction.
    // dialog.remove();
    // didUnmount();
    closeDialog(dialog, beforeUnmount, didUnmount);
    zoomClickListener();

    $(document).off('keydown.closeZoom');

    // bring back NTF
    $('.js-fixed-corner').show();
  };

/**
 * Simple rendering when no images are found.
 * @private
 * @param  {String} noImages - Text indicating no images were found
 * @return {string} Template containing content around no visible images
 */
const renderNoImages = ({ noImages }) => `
  <p class="${CONTENT_CLASSNAME}">${noImages}</p>
`;

/**
 * Template containing what is to be rendered in the dialog.
 * @private
 * @param  {array} images - A list of images
 * @param  {Function} [menuRender=renderNoop] - Used for additional rendering
 * @param  {Function} [beforeContentRender=renderNoop] - Optional rendering
 * before the main content
 * @return {String} A fully rendered template as a string
 */
const renderDialog = ({ images, videos, noImages, menuRender = renderNoop, beforeContentRender = renderNoop }) => `
    ${beforeContentRender()}
    <div id="zoom-menu" class="zoom-menu">
      <span class="zoom-menu__extra">${menuRender()}</span>
      <button id="${CLOSE_ID}" class="zoom-menu__close">
        <span class="icon icon--md icon--x-icon">
          <svg aria-hidden="true" focusable="false" viewBox="0 0 16 16">
            <path d="M9.06 8l2.72-2.72A.75.75 0 0 0 10.72 4.22L8 6.94 5.28 4.22A.75.75 0 0 0 4.22 5.28L6.94 8l-2.72 2.72A.75.75 0 1 0 5.28 11.78L8 9.06l2.72 2.72a.75.75 0 0 0 1.061-1.061z"/>
          </svg>
        </span>
      </button>
    </div>
    <div id="${ZOOM_SLIDER_ID}" class="zoom-slider u-margin-l--none">
      ${images.length
        ?
        images.map((source, i) => {
          if (source.includes('.mp4')) {
            return `<div><video class="zoom-slider__item" id="${getSlideId(i)}" data-video="true" muted loop='loop' playsinline>
              <source src="${source}" type="video/mp4">
            </video></div>`;
          }

          return `<div><img src="${source}" class="zoom-slider__item" id="${getSlideId(i)}"></div>`;
        }).join('')
        :
        renderNoImages({ noImages })
      }
      ${videos.length
        ?
        videos.map((video, i) =>
          `<li><video class="zoom-slider__item" id="${getSlideId(i + images.length)}" data-video="true" controls>
            <source src="${video}" type="video/mp4">
          </video></li>`,
        ).join('')
        :
        ''
      }
    </div>
  `;

const renderDialogAB = ({ images, thumbnails, noImages, menuRender = renderNoop }) => {
  const generateThumbnailsContent = () => {
    if (thumbnails && thumbnails.length) {
      return thumbnails.map((thumbnail, i) =>
        `<button tabindex="0" class="horizontal-scroll__item horizontal-scroll__item--zoom-thumb u-relative"><img src="${thumbnail.detailThumbPath}" srcset="${thumbnail.detailThumbPath} 1x, ${thumbnail.detailThumbPath2x} 2x" id="${getSlideId(i)}"></button>`,
      ).join('');
    } else if (images && images.length) {
      return images.map((image, i) =>
        `<button tabindex="0" class="horizontal-scroll__item horizontal-scroll__item--zoom-thumb u-relative"><img src="${image}" id="${getSlideId(i)}"></button>`,
      ).join('');
    }
    return renderNoImages({ noImages });
  };

  // horizontal scroll
  return `
    <div class="zoom-slider--ab">
      <div class="zoom-slider__wrapper">
        <div id="${ZOOM_SLIDER_ID}" class="zoom-slider u-margin-l--none">
          ${images.length ? images.map((image, i) => {
            if (image.includes('.mp4')) {
              return `<video class="zoom-slider__item" id="${getSlideId(i)}" data-video="true" muted loop='loop' playsinline>
                            <source src="${image}" type="video/mp4">
                          </video>`;
            }
            return `<img src="${image}" class="zoom-slider__item" id="${getSlideId(i)}">`;
          }).join('') : renderNoImages({ noImages })}
        </div>
      </div>
      <div class="u-relative u-margin-b--xl u-margin-t--auto">
        <div class="u-flex u-flex-align-center u-padding-l--lg">
          <div style="flex: 0;">
            ${menuRender()}
          </div>
          <div class="horizontal-scroll u-margin-r--lg u-margin-l--md">
            <div id="js-horizontal-scroll-thumbs" class="horizontal-scroll__item-wrap" ${images.length <= 5 ? 'style="align-items: center; justify-content: center;"' : ''}>
              ${generateThumbnailsContent()}
            </div>
          </div>
          <button id="${CLOSE_ID}" class="u-padding-a--xl" style="flex: 0;">
            <span class="icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><path d="M9.414 8l6.293-6.293A1 1 0 0 0 14.293.293L8 6.586 1.707.293A1 1 0 0 0 .293 1.707L6.586 8 .293 14.293a1 1 0 1 0 1.414 1.414L8 9.414l6.293 6.293a1 1 0 0 0 1.414-1.414z"/></svg>
            </span>
          </button>
        </div>
      </div>
    </div>
  `;
};

/**
 * Merges or concats options to be passed along to slick.
 * @private
 * @param  {Object} [options={}] - Options sent along to slick
 * @return {Object} A mergingin of options for slick
 */
const mergeSliderOptions = (options = {}) =>
  $.extend({
    arrows: false,
    dots: true,
    infinite: true,
    slidesToShow: 1,
    rtl: _get(window, 'rcProps.isRtl', false),
    appendDots: '#zoom-menu',
    dotsClass: 'slider-dots__menu image-carousel__dots',
    slide: 'li',
    customPaging,
  }, options);

// When the slide is changing
function playPauseVideo(slick, control) {
  const currentSlide = slick.find('.slick-current');
  const player = currentSlide.find('iframe').get(0) || currentSlide.find('video').get(0);

  if (!player) return;

  if (player !== null) {
    if (control === 'play') {
      player.play();
    } else {
      player.pause();
    }
  }
}

const focusOnStartingIndex = (dialog, startingIndex) => {
  if ($('.focus-active').length) {
    dialog.$el.find('.horizontal-scroll__item').eq(startingIndex).focus();
  }
};

const logClickThumbnail = (index) => {
  if (typeof window.logEngagementWithDetails === 'function') {
    window.logEngagementWithDetails(window.location.href, 'pdp zoom modal', 'click thumbnail',
        `thumb index ${index} (0-based)`);
  }
};

/**
 * Responsible for rendering a slider zoom. More specifically, it will create
 * a dialog/modal such that n number of images are used for creating a slider.
 * On top of that, each image will have the ability for pinch/zoom/moving
 * around an image such that the image can be examined by the end user.
 * @see DEFAULTS
 * @param {array} images - A list of images used for the slider and zooming
 * @param {Object} options - Additional hooks to interact with the slider zoom
 * @return {Promise} Denotes when the slier zoom has been fully rendered
 */
export default function sliderZoom(images, options = {}) {
  const {
    startingIndex,
    speed,
    slideFunc,
    menuRender,
    didRender,
    beforeUnmount,
    didUnmount,
    loadHammerJS,
    didSlideTransition,
    noImages,
    Dialog,
    zoomClickListener,
    beforeContentRender,
    imageLoaded,
    abTest,
    thumbnails,
    trapFocusFunc,
  } = $.extend({}, DEFAULTS, options);
  const dialog = new Dialog();
  const videos = options.videos || [];
  return loadHammerJS().then((Hammer) => {
    // hide NTF
    $('.js-fixed-corner').hide();
    let output;

    if (abTest) {
      output = renderDialogAB({
        images,
        thumbnails,
        menuRender,
        noImages,
        beforeContentRender,
      });
    } else {
      output = renderDialog({ images, videos, menuRender, noImages, beforeContentRender });
    }
    dialog
      .render(output, abTest)
      .then(($dialog) => {
        preloadImages(images, startingIndex).then((img) => {
          imageLoaded($dialog, img);
        });
        trapFocusFunc($dialog);
      });

    didRender();

    if (!images.length) {
      bindClose(() => {
        closeDialog(dialog, beforeUnmount, didUnmount);
      });
      return;
    }

    slideFunc(startingIndex);

    const $zoomSlider = $(document.getElementById(ZOOM_SLIDER_ID));
    // const $zoomThumb = $(document.getElementById(ZOOM_THUMB_ID));
    let currentSliderItem = getSlide(startingIndex);
    let hammerInstance = imageZoom(Hammer, currentSliderItem);
    let currentNum = startingIndex;
    let previous;
    let isVideo = false;
    let mergeSliderOptionsParams;

    const dotLength = $zoomSlider.children().length;

    if (abTest) {
      mergeSliderOptionsParams = {
        // asNavFor: `#${ZOOM_THUMB_ID}`,
        speed,
        initialSlide: startingIndex,
        dots: false,
        infinite: false,
        swipeToSlide: true,
      };

      $('.horizontal-scroll__item--zoom-thumb').eq(startingIndex).addClass('active');

      $zoomSlider.on('beforeChange', (event, slick, currentSlide, nextSlide) => {
        $('.horizontal-scroll__item--zoom-thumb').removeClass('active');
        $('.horizontal-scroll__item--zoom-thumb').eq(nextSlide).addClass('active');
      });

      // scroll horizontal slider if active goes out of view
      $zoomSlider.on('afterChange', () => {
        const sliderBeginningPosition = $('.zoom-slider--ab .horizontal-scroll').position().left + 8;
        const $zoomThumb = $('#js-horizontal-scroll-thumbs');
        const active = $('.horizontal-scroll__item--zoom-thumb.active');
        const activeWidth = active.width();
        const sliderWidth = $zoomThumb.width();
        const sliderPosition = $zoomThumb.position().left;
        // const sliderStart = sliderPosition;
        const sliderEnd = sliderBeginningPosition + sliderWidth;
        const activePosition = active.position().left;
        const activeEnd = activePosition + activeWidth;
        const activeStart = activePosition;

        if (activeEnd > sliderEnd) {
          $('.horizontal-scroll').animate({ scrollLeft: Math.abs(sliderPosition) + 150 }, 300);
        } else if (activeStart < sliderBeginningPosition) {
          $('.horizontal-scroll').animate({ scrollLeft: sliderPosition - 150 }, 300);
        }
      });
    } else {
      mergeSliderOptionsParams = {
        speed,
        initialSlide: startingIndex,
        dots: (dotLength > 1),
      };
    }

    $zoomSlider
      .on('init', (evt, slick) => {
        slick.$slides.each((i, slide) => {
          if ($(slide).children('video').length) {
            $('.zoom-menu .image-carousel__dots li a').eq(i).addClass('image-carousel__dot-button--vid');
          }
        });

        images.forEach((value, index) => {
          // if slide is video type, change the pagination to
          // show the play button instead of the default circle
          if (value.toLowerCase().endsWith('.mp4')) {
            $('#zoom-menu .slider-dots__menu li button').eq(index).addClass('image-carousel__dot-button--vid');
          }
        });
        playPauseVideo($zoomSlider, 'play');
      })
      .on('afterChange', (evt, slide, currentSlide) => {
        // when at the beginning or end, do not reset zooming
        if (currentNum === currentSlide) {
          return;
        }
        currentNum = currentSlide;
        slideFunc(currentNum);
        previous = currentSliderItem;
        hammerInstance.destroy();
        beforeUnmount();

        currentSliderItem = getSlide(currentNum);
        isVideo = $(currentSliderItem).data('video');
        if (!isVideo) {
          hammerInstance = imageZoom(Hammer, currentSliderItem);
        }

        // use a setTimeout instead of the afterChange event since it'll
        // provide to long of a delay between swipe change when the image
        // has been zoomed in too much, yeah, a whole lot of edge cases
        // happening here.
        setTimeout(() => {
          /* eslint-disable no-param-reassign */
          previous.style.transform = 'none';
          /* eslint-enable no-param-reassign */
          didSlideTransition(previous, currentSliderItem);
        }, speed);

        // video play/pause
        playPauseVideo($zoomSlider, 'pause');
      }).on('beforeChange', (event, slick) => {
        const $slick = $(slick.$slider);
        playPauseVideo($slick, 'pause');
      }).on('afterChange', (event, slick) => {
        const $slick = $(slick.$slider);
        playPauseVideo($slick, 'play');
      });

    $zoomSlider.slick(mergeSliderOptions(mergeSliderOptionsParams));

    if (abTest) {
      $('.horizontal-scroll__item--zoom-thumb').on('click', function () {
        $('.horizontal-scroll__item--zoom-thumb').removeClass('active');
        $(this).addClass('active');
        logClickThumbnail($(this).index());
        $zoomSlider.slick('slickGoTo', $(this).index());
      });
    }

    focusOnStartingIndex(dialog, startingIndex);
    bindClose(teardownFull($zoomSlider, hammerInstance, dialog, beforeUnmount,
      didUnmount, zoomClickListener));
  });
}



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/zoom/slider-zoom.js